import { useRecoilState } from 'recoil';

import {
    createPalette,
    PaletteButton,
    PaletteCommandType,
    PalettePositionMaxHeight,
} from '@gitbook/react-uikit';
import { Icon } from '@gitbook/spine-icons';
import {
    Button,
    ButtonGroup,
    type ButtonProps,
    DialogRoot,
    useDialogTriggerState,
} from '@gitbook/spine-react';
import { createFastCacheKeyWithCollisionRisk } from '@gitbook/util-suspense-cache';

import { apiResource, readAPICache } from '../../state';
import {
    AdaptiveContentVisitorSegmentsDialog,
    DEFAULT_SEGMENT_TITLE,
} from './AdaptiveContentVisitorSegmentsDialog';
import { siteVisitorSegmentSelector } from './state';
import { useUserDefinedVisitorSegments } from './useUserDefinedVisitorSegments';

const DEFAULT_TITLE = 'Default experience';

/**
 * Button to select the visitor claims to use for the preview of a site.
 */
export function SiteVisitorClaimsButton(
    props: {
        organizationId: string;
        siteId: string;
    } & Partial<ButtonProps>
) {
    const { organizationId, siteId, ...buttonProps } = props;
    const { userDefinedSegments, addVisitorSegment } = useUserDefinedVisitorSegments();
    const [visitorSegment, setVisitorSegment] = useRecoilState(siteVisitorSegmentSelector(siteId));

    const visitorSegmentsDialogTrigger = useDialogTriggerState();

    return (
        <ButtonGroup kind="segmented">
            <PaletteButton
                kind="secondary"
                {...buttonProps}
                className={visitorSegment ? 'w-32 truncate' : 'w-[150px]'}
                label={visitorSegment?.title ?? DEFAULT_TITLE}
                trailingIcon={Icon.ChevronDown}
                palette={() => {
                    return createPalette({
                        position: {
                            maxHeight: PalettePositionMaxHeight.AUTO,
                        },
                        groups: [
                            {
                                key: 'defaultSegmentGroup',
                                commands: [
                                    {
                                        key: 'default',
                                        title: DEFAULT_TITLE,
                                        selected: visitorSegment === null,
                                        type: PaletteCommandType.RADIO,
                                        trigger: () => {
                                            setVisitorSegment(null);
                                        },
                                    },
                                ],
                            },
                            {
                                key: 'userDefinedSegmentsGroup',
                                title: 'Custom segments',
                                commands: () => {
                                    return (userDefinedSegments || []).map((segment) => {
                                        return {
                                            key: String(segment.id),
                                            title: segment.title,
                                            type: PaletteCommandType.RADIO,
                                            selected: visitorSegment?.id === segment.id,
                                            trigger: () => {
                                                setVisitorSegment({
                                                    type: 'user-defined',
                                                    ...segment,
                                                });
                                            },
                                        };
                                    });
                                },
                            },
                            {
                                key: 'detectedSegmentsGroup',
                                title: 'Detected segments',
                                commands: () => {
                                    const data = readAPICache(
                                        apiResource('listSiteVisitorSegments', {
                                            organizationId,
                                            siteId,
                                        })
                                    );
                                    return data.items.map((segment) => {
                                        const segmentId = createFastCacheKeyWithCollisionRisk(
                                            segment.claims
                                        );
                                        return {
                                            key: segmentId,
                                            title: segment.title,
                                            type: PaletteCommandType.RADIO,
                                            selected: visitorSegment?.id === segmentId,
                                            trigger: () => {
                                                setVisitorSegment({
                                                    id: segmentId,
                                                    type: 'detected',
                                                    ...segment,
                                                });
                                            },
                                        };
                                    });
                                },
                            },
                            {
                                key: 'newSegmentGroup',
                                commands: [
                                    {
                                        key: 'newSegment',
                                        title: 'New segment',
                                        icon: Icon.Download,
                                        trigger: async () => {
                                            const addedSegment = await addVisitorSegment({
                                                title: DEFAULT_SEGMENT_TITLE,
                                                claims: {},
                                            });
                                            setVisitorSegment({
                                                type: 'user-defined',
                                                ...addedSegment,
                                            });
                                            visitorSegmentsDialogTrigger.open();
                                        },
                                    },
                                ],
                            },
                        ],
                    });
                }}
            />
            {visitorSegment ? (
                <>
                    <Button
                        kind="secondary"
                        {...buttonProps}
                        iconOnly
                        leadingIcon={Icon.Settings}
                        label="Edit segment"
                        onPress={async () => {
                            if (visitorSegment.type === 'detected') {
                                const addedSegment = await addVisitorSegment({
                                    title: `${visitorSegment.title} copy`,
                                    claims: visitorSegment.claims,
                                });
                                setVisitorSegment({
                                    type: 'user-defined',
                                    ...addedSegment,
                                });
                            }
                            visitorSegmentsDialogTrigger.open();
                        }}
                    />
                    <DialogRoot {...visitorSegmentsDialogTrigger.rootProps}>
                        <AdaptiveContentVisitorSegmentsDialog
                            organizationId={organizationId}
                            siteId={siteId}
                            selectedSegment={visitorSegment}
                            onConfirm={(selectedSegment) => setVisitorSegment(selectedSegment)}
                        />
                    </DialogRoot>
                </>
            ) : null}
        </ButtonGroup>
    );
}
